import React, { useState, useEffect } from 'react';
import { databaseService } from '../../services/databaseService';

const DatabaseExplorer = () => {
  const [schemas, setSchemas] = useState([]);
  const [selSchema, setSelSchema] = useState('public');
  const [tables, setTables] = useState([]);
  const [selTable, setSelTable] = useState(null);
  const [tableStruct, setTableStruct] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tab, setTab] = useState('browse');
  const [sqlQuery, setSqlQuery] = useState('');
  const [sqlResult, setSqlResult] = useState(null);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [addFormData, setAddFormData] = useState({});
  const [editRowIdx, setEditRowIdx] = useState(null);
  const [editData, setEditData] = useState({});
  const [cmpSchema2, setCmpSchema2] = useState('public');
  const [cmpTable2, setCmpTable2] = useState(null);
  const [cmpResult, setCmpResult] = useState(null);
  const [cmpLoading, setCmpLoading] = useState(false);
  const [tables2, setTables2] = useState([]);
  const [rowCounts, setRowCounts] = useState({});
  const [tableSearch, setTableSearch] = useState('');
  const [dataSearch, setDataSearch] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [newSchemaName, setNewSchemaName] = useState('');
  const [newTableName, setNewTableName] = useState('');
  const [newTableSchema, setNewTableSchema] = useState('public');
  const [newTableCols, setNewTableCols] = useState([{name:'id', type:'SERIAL PRIMARY KEY'}, {name:'', type:'TEXT'}]);
  const [createStatus, setCreateStatus] = useState(null);
  const PAGE_SIZE = 30;

  useEffect(() => { loadSchemas(); }, []);
  useEffect(() => { if (selSchema) loadTables(selSchema); }, [selSchema]);
  useEffect(() => { if (selTable && selSchema) { loadData(dataSearch); loadStruct(); } }, [selTable, selSchema, page]);

  const loadSchemas = async () => {
    try {
      const s = await databaseService.listSchemas();
      setSchemas(s);
    } catch (e) { setError(e.message); }
  };

  const loadTables = async (schema) => {
    try {
      setLoading(true);
      const t = await databaseService.listTables(schema);
      setTables(t);
      const counts = {};
      for (const tbl of t) {
        try {
          const d = await databaseService.getTableData(schema, tbl, 1, 0);
          counts[tbl] = d.totalCount;
        } catch { counts[tbl] = 0; }
      }
      setRowCounts(counts);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadData = async (searchTerm) => {
    try {
      setLoading(true); setError(null);
      const d = await databaseService.getTableData(selSchema, selTable, PAGE_SIZE, page * PAGE_SIZE, searchTerm || '');
      setRows(d.rows || []);
      setTotalCount(d.totalCount || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadStruct = async () => {
    try {
      const s = await databaseService.getTableStructure(selSchema, selTable);
      setTableStruct(s || []);
    } catch (e) { console.error(e); }
  };

  const handleSelectTable = (t) => { setSelTable(t); setPage(0); setEditRowIdx(null); setAddFormData({}); setTab('browse'); setDataSearch(''); };

  const handleGlobalSearch = async () => {
    if (!globalSearch.trim()) return;
    try {
      setGlobalLoading(true); setGlobalResults(null);
      const results = [];
      for (const schema of schemas) {
        const tbls = schema === selSchema ? tables : await databaseService.listTables(schema);
        for (const tbl of tbls) {
          try {
            const struct = await databaseService.getTableStructure(schema, tbl);
            const textCols = struct.filter(c => ['character varying','text','varchar','char','name'].includes(c.data_type)).map(c => `"${c.column_name}"`);
            if (textCols.length === 0) continue;
            const whereParts = textCols.map(c => `${c}::text ILIKE '%${globalSearch.replace(/'/g,"''").trim()}%'`);
            const q = `SELECT * FROM ${schema}.${tbl} WHERE ${whereParts.join(' OR ')} LIMIT 5`;
            const r = await databaseService.executeQuery(q);
            if (r.rows && r.rows.length > 0) {
              results.push({ schema, table: tbl, rows: r.rows, columns: r.columns, matchCount: r.rows.length });
            }
          } catch { /* skip */ }
        }
      }
      setGlobalResults(results);
    } catch (e) { setError(e.message); }
    finally { setGlobalLoading(false); }
  };

  const handleAddRow = async () => {
    try {
      setError(null);
      await databaseService.insertRow(selSchema, selTable, addFormData);
      setSuccess('Satir basariyla eklendi!');
      setAddFormData({});
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.message); }
  };

  const handleUpdateRow = async (row) => {
    try {
      setError(null);
      const pk = tableStruct.find(c => c.column_name === 'id') ? 'id' : tableStruct[0]?.column_name;
      if (!pk) return;
      const pkVal = row[pk];
      const where = typeof pkVal === 'number' ? `"${pk}" = ${pkVal}` : `"${pk}" = '${pkVal}'`;
      await databaseService.updateRow(selSchema, selTable, editData, where);
      setSuccess('Satir guncellendi!');
      setEditRowIdx(null); setEditData({});
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.message); }
  };

  const handleDeleteRow = async (row) => {
    if (!window.confirm('Bu satiri silmek istediginizden emin misiniz?')) return;
    try {
      setError(null);
      const pk = tableStruct.find(c => c.column_name === 'id') ? 'id' : tableStruct[0]?.column_name;
      if (!pk) return;
      const pkVal = row[pk];
      const where = typeof pkVal === 'number' ? `"${pk}" = ${pkVal}` : `"${pk}" = '${pkVal}'`;
      await databaseService.deleteRow(selSchema, selTable, where);
      setSuccess('Satir silindi!');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.message); }
  };

  const handleRunSql = async () => {
    try {
      setSqlLoading(true); setError(null); setSqlResult(null);
      const trimmed = sqlQuery.trim().toUpperCase();
      let result;
      if (trimmed.startsWith('SELECT')) {
        result = await databaseService.executeQuery(sqlQuery);
        setSqlResult({ type: 'select', ...result });
      } else {
        result = await databaseService.executeWriteQuery(sqlQuery);
        setSqlResult({ type: 'write', ...result });
        if (selTable) loadData();
      }
    } catch (e) { setError(e.message); }
    finally { setSqlLoading(false); }
  };

  const handleCompare = async () => {
    if (!selTable || !cmpTable2) return;
    try {
      setCmpLoading(true); setError(null);
      const r = await databaseService.compareTables(selSchema, selTable, cmpSchema2, cmpTable2);
      setCmpResult(r);
    } catch (e) { setError(e.message); }
    finally { setCmpLoading(false); }
  };

  useEffect(() => {
    if (cmpSchema2) {
      databaseService.listTables(cmpSchema2).then(setTables2).catch(() => setTables2([]));
    }
  }, [cmpSchema2]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const S = {
    wrap: { display:'flex', height:'calc(100vh - 200px)', gap:0, background:'#f8fafc', borderRadius:12, overflow:'hidden', border:'1px solid #e2e8f0' },
    side: { width:280, background:'#fff', borderRight:'1px solid #e2e8f0', display:'flex', flexDirection:'column', overflow:'hidden' },
    sideHead: { padding:'20px 16px 12px', borderBottom:'1px solid #e2e8f0' },
    sideTitle: { fontSize:15, fontWeight:700, color:'#1e293b', margin:0 },
    schemaRow: { display:'flex', gap:6, padding:'8px 16px', flexWrap:'wrap' },
    schemaBtn: (a) => ({ padding:'4px 12px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, background: a?'#3b82f6':'#f1f5f9', color: a?'#fff':'#64748b' }),
    tableList: { flex:1, overflow:'auto', padding:'4px 0' },
    tableBtn: (a,hasData) => ({ display:'flex', justifyContent:'space-between', alignItems:'center', width:'100%', padding:'10px 16px', border:'none', cursor:'pointer', fontSize:13, fontWeight: a?600:400, background: a?'#eff6ff':'transparent', color: a?'#1d4ed8':'#334155', borderLeft: a?'3px solid #3b82f6':'3px solid transparent', transition:'all .15s' }),
    badge: (c) => ({ fontSize:11, padding:'2px 8px', borderRadius:10, fontWeight:600, background:c==='empty'?'#f1f5f9':'#dcfce7', color:c==='empty'?'#94a3b8':'#166534' }),
    main: { flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
    mainHead: { padding:'16px 24px', background:'#fff', borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center' },
    tabs: { display:'flex', gap:4, padding:'8px 24px', background:'#fff', borderBottom:'1px solid #e2e8f0' },
    tabBtn: (a) => ({ padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background: a?'#3b82f6':'transparent', color: a?'#fff':'#64748b', transition:'all .15s' }),
    content: { flex:1, overflow:'auto', padding:24 },
    alert: (t) => ({ padding:'12px 16px', borderRadius:8, marginBottom:16, fontSize:13, fontWeight:500, background: t==='err'?'#fef2f2':'#f0fdf4', color: t==='err'?'#dc2626':'#16a34a', border:`1px solid ${t==='err'?'#fecaca':'#bbf7d0'}` }),
    input: { padding:'8px 12px', borderRadius:6, border:'1px solid #d1d5db', fontSize:13, outline:'none', width:'100%', transition:'border .2s' },
    btn: (c) => ({ padding:'8px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background:c||'#3b82f6', color:'#fff', transition:'all .15s' }),
    btnSm: (c) => ({ padding:'4px 10px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:500, background:c||'#3b82f6', color:'#fff' }),
  };

  return (
    <div style={S.wrap}>
      <div style={S.side}>
        <div style={S.sideHead}>
          <h3 style={S.sideTitle}>Veritabani Yonetimi</h3>
          <p style={{fontSize:12, color:'#64748b', margin:'4px 0 0'}}>Canli DB uzerinde islem yapin</p>
        </div>
        <div style={S.schemaRow}>
          {schemas.map(s => (
            <button key={s} style={S.schemaBtn(s===selSchema)} onClick={() => { setSelSchema(s); setSelTable(null); setTableSearch(''); }}>{s}</button>
          ))}
          <button style={{...S.schemaBtn(false), background:'#dbeafe', color:'#2563eb', fontSize:14, lineHeight:1}} onClick={() => { setSelTable(null); setTab('create'); }} title="Yeni Sema / Tablo Olustur">+</button>
        </div>
        <div style={{padding:'8px 12px', borderBottom:'1px solid #e2e8f0'}}>
          <div style={{position:'relative'}}>
            <input
              style={{...S.input, paddingLeft:32, fontSize:12}}
              placeholder="Tablo ara..."
              value={tableSearch}
              onChange={e => setTableSearch(e.target.value)}
            />
            <span style={{position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'#94a3b8', pointerEvents:'none'}}></span>
          </div>
        </div>
        <div style={S.tableList}>
          {tables.filter(t => !tableSearch || t.toLowerCase().includes(tableSearch.toLowerCase())).map(t => (
            <button key={t} style={S.tableBtn(t===selTable, rowCounts[t]>0)} onClick={() => handleSelectTable(t)}>
              <span>{t}</span>
              <span style={S.badge(rowCounts[t]>0?'data':'empty')}>{rowCounts[t]||0}</span>
            </button>
          ))}
          {tables.filter(t => !tableSearch || t.toLowerCase().includes(tableSearch.toLowerCase())).length===0 && !loading && <p style={{padding:16, color:'#94a3b8', fontSize:13, textAlign:'center'}}>{tableSearch ? `"${tableSearch}" icin tablo bulunamadi` : 'Tablo bulunamadi'}</p>}
        </div>
      </div>

      <div style={S.main}>
        {!selTable && tab !== 'create' ? (
          <div style={{flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:16, padding:40}}>
            <span style={{fontSize:48}}>{''}</span>
            <h3 style={{color:'#64748b', fontWeight:500, margin:0}}>Bir tablo secin veya arama yapin</h3>
            <p style={{color:'#94a3b8', fontSize:13, margin:0}}>Sol panelden tablo secip veri goruntuleyin veya tum veritabaninda arama yapin</p>
            <div style={{display:'flex', gap:8, width:'100%', maxWidth:500, marginTop:8}}>
              <input
                style={{...S.input, flex:1, fontSize:14, padding:'12px 16px'}}
                placeholder="Tum veritabaninda ara... (Enter ile arama)"
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleGlobalSearch(); }}
              />
              <button style={S.btn(globalLoading ? '#94a3b8' : '#3b82f6')} onClick={handleGlobalSearch} disabled={globalLoading}>
                {globalLoading ? 'Araniyor...' : 'Ara'}
              </button>
            </div>
            {globalLoading && <p style={{fontSize:13, color:'#3b82f6', margin:0}}>Tum tablolar taraniyor, bu biraz zaman alabilir...</p>}
            {globalResults !== null && (
              <div style={{width:'100%', maxWidth:800, marginTop:12}}>
                {globalResults.length === 0 ? (
                  <div style={{...S.alert('err'), textAlign:'center'}}>"{globalSearch}" icin sonuc bulunamadi.</div>
                ) : (
                  <>
                    <div style={{fontSize:13, color:'#16a34a', fontWeight:600, marginBottom:12}}>{globalResults.reduce((s,r) => s + r.matchCount, 0)} sonuc bulundu ({globalResults.length} tabloda)</div>
                    {globalResults.map((gr, gi) => (
                      <div key={gi} style={{background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, marginBottom:12, overflow:'hidden'}}>
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 16px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0'}}>
                          <span style={{fontSize:13, fontWeight:700, color:'#1e293b'}}>{gr.schema}.{gr.table}</span>
                          <button style={S.btnSm('#3b82f6')} onClick={() => { setSelSchema(gr.schema); setTimeout(() => handleSelectTable(gr.table), 100); }}>Tabloya Git</button>
                        </div>
                        <div style={{overflowX:'auto'}}>
                          <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                            <thead><tr style={{background:'#f8fafc'}}>{gr.columns.map((c,i) => <th key={i} style={{padding:'6px 10px', textAlign:'left', fontWeight:600, color:'#475569', borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap'}}>{c}</th>)}</tr></thead>
                            <tbody>{gr.rows.map((r,ri) => <tr key={ri} style={{borderBottom:'1px solid #f1f5f9'}}>{gr.columns.map((c,ci) => {
                              const val = r[c] !== null && r[c] !== undefined ? String(r[c]) : '';
                              const isMatch = val.toLowerCase().includes(globalSearch.toLowerCase());
                              return <td key={ci} style={{padding:'5px 10px', color:'#334155', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', background: isMatch ? '#fef9c3' : 'transparent', fontWeight: isMatch ? 600 : 400}}>{val.substring(0,60) || <span style={{color:'#cbd5e1'}}>null</span>}</td>;
                            })}</tr>)}</tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        ) : tab === 'create' && !selTable ? (
          <div style={{flex:1, overflow:'auto', padding:24}}>
            {error && <div style={S.alert('err')}>{error}</div>}
            {success && <div style={S.alert('ok')}>{success}</div>}
            {renderCreate()}
          </div>
        ) : (
          <>
            <div style={S.mainHead}>
              <div>
                <h3 style={{margin:0, fontSize:16, color:'#1e293b'}}>{selSchema}.{selTable}</h3>
                <p style={{margin:'2px 0 0', fontSize:12, color:'#64748b'}}>{totalCount} kayit  |  {tableStruct.length} kolon</p>
              </div>
              <div style={{display:'flex', gap:8, alignItems:'center'}}>
                <button style={S.btn('#10b981')} onClick={loadData}>Yenile</button>
              </div>
            </div>

            <div style={S.tabs}>
              {[['browse','Veri Goruntule'],['add','Yeni Kayit Ekle'],['create','Yeni Olustur'],['sql','SQL Sorgusu'],['compare','Tablo Kiyasla']].map(([id,label]) => (
                <button key={id} style={S.tabBtn(tab===id)} onClick={() => setTab(id)}>{label}</button>
              ))}
            </div>

            <div style={S.content}>
              {error && <div style={S.alert('err')}>{error}</div>}
              {success && <div style={S.alert('ok')}>{success}</div>}

              {tab==='browse' && renderBrowse()}
              {tab==='add' && renderAdd()}
              {tab==='sql' && renderSql()}
              {tab==='compare' && renderCompare()}
              {tab==='create' && renderCreate()}
            </div>
          </>
        )}
      </div>
    </div>
  );

  function renderBrowse() {
    if (loading) return <div style={{textAlign:'center', padding:40, color:'#64748b'}}>Yukleniyor...</div>;

    const filteredRows = rows;

    const handleSearch = () => {
      setPage(0);
      loadData(dataSearch);
    };

    return (
      <>
        <div style={{display:'flex', gap:12, marginBottom:16, alignItems:'center'}}>
          <div style={{position:'relative', flex:1, maxWidth:400}}>
            <input
              style={{...S.input, paddingLeft:32, fontSize:13}}
              placeholder={`${selTable} tablosunda ara... (Enter)`}
              value={dataSearch}
              onChange={e => setDataSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            />
            <span style={{position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'#94a3b8', pointerEvents:'none'}}></span>
          </div>
          <button style={S.btnSm('#3b82f6')} onClick={handleSearch}>Ara</button>
          {dataSearch && (
            <span style={{fontSize:12, color: '#475569', fontWeight:600}}>
              {totalCount} sonuc
            </span>
          )}
          {dataSearch && <button style={S.btnSm('#64748b')} onClick={() => { setDataSearch(''); setPage(0); loadData(''); }}>Temizle</button>}
        </div>
        {filteredRows.length === 0 ? (
          <div style={{textAlign:'center', padding:40, color:'#94a3b8'}}>
            {dataSearch ? `"${dataSearch}" icin sonuc bulunamadi.` : 'Bu tabloda veri yok. "Yeni Kayit Ekle" sekmesinden veri ekleyebilirsiniz.'}
          </div>
        ) : (
        <>
        <div style={{overflowX:'auto', border:'1px solid #e2e8f0', borderRadius:8}}>
          <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
            <thead>
              <tr style={{background:'#f8fafc'}}>
                {tableStruct.map((c,i) => <th key={i} style={{padding:'10px 12px', textAlign:'left', fontWeight:600, color:'#475569', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap'}}>{c.column_name}<br/><span style={{fontSize:10, color:'#94a3b8', fontWeight:400}}>{c.data_type}</span></th>)}
                <th style={{padding:'10px 12px', textAlign:'center', fontWeight:600, color:'#475569', borderBottom:'2px solid #e2e8f0', width:120}}>Islemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row,ri) => {
                const origIdx = rows.indexOf(row);
                return (
                <tr key={ri} style={{borderBottom:'1px solid #f1f5f9'}}>
                  {tableStruct.map((c,ci) => {
                    const val = row[c.column_name];
                    const strVal = val !== null && val !== undefined ? String(val) : '';
                    const isMatch = dataSearch && strVal.toLowerCase().includes(dataSearch.toLowerCase());
                    return (
                    <td key={ci} style={{padding:'8px 12px', color:'#334155', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', background: isMatch ? '#fef9c3' : 'transparent'}}>
                      {editRowIdx===origIdx ? (
                        <input style={S.input} value={editData[c.column_name]!==undefined ? editData[c.column_name] : (val??'')} onChange={e => setEditData(p => ({...p, [c.column_name]: e.target.value}))} />
                      ) : (
                        val!==null&&val!==undefined ? strVal.substring(0,80) : <span style={{color:'#cbd5e1', fontStyle:'italic'}}>null</span>
                      )}
                    </td>
                    );
                  })}
                  <td style={{padding:'8px 12px', textAlign:'center'}}>
                    <div style={{display:'flex', gap:4, justifyContent:'center'}}>
                      {editRowIdx===origIdx ? (
                        <>
                          <button style={S.btnSm('#10b981')} onClick={() => handleUpdateRow(row)}>Kaydet</button>
                          <button style={S.btnSm('#64748b')} onClick={() => { setEditRowIdx(null); setEditData({}); }}>Iptal</button>
                        </>
                      ) : (
                        <>
                          <button style={S.btnSm('#f59e0b')} onClick={() => { setEditRowIdx(origIdx); setEditData({}); }}>Duzenle</button>
                          <button style={S.btnSm('#ef4444')} onClick={() => handleDeleteRow(row)}>Sil</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:12, marginTop:16}}>
            <button style={S.btn(page===0?'#cbd5e1':'#3b82f6')} onClick={() => setPage(p=>Math.max(0,p-1))} disabled={page===0}>Onceki</button>
            <span style={{fontSize:13, color:'#64748b'}}>Sayfa {page+1} / {totalPages}</span>
            <button style={S.btn(page>=totalPages-1?'#cbd5e1':'#3b82f6')} onClick={() => setPage(p=>p+1)} disabled={page>=totalPages-1}>Sonraki</button>
          </div>
        )}
        </>
        )}
      </>
    );
  }

  function renderAdd() {
    return (
      <div style={{maxWidth:700}}>
        <h4 style={{color:'#1e293b', marginBottom:16}}>Yeni Kayit Ekle: {selTable}</h4>
        <div style={{display:'grid', gridTemplateColumns:'1fr 2fr', gap:'12px 16px', alignItems:'center'}}>
          {tableStruct.map((c,i) => (
            <React.Fragment key={i}>
              <label style={{fontSize:13, fontWeight:600, color:'#475569'}}>{c.column_name} <span style={{fontSize:11, color:'#94a3b8'}}>({c.data_type})</span></label>
              <input style={S.input} placeholder={c.is_nullable==='YES'?'(opsiyonel)':'(zorunlu)'} value={addFormData[c.column_name]||''} onChange={e => setAddFormData(p => ({...p, [c.column_name]: e.target.value}))} />
            </React.Fragment>
          ))}
        </div>
        <div style={{marginTop:20, display:'flex', gap:8}}>
          <button style={S.btn('#10b981')} onClick={handleAddRow}>Kaydet</button>
          <button style={S.btn('#64748b')} onClick={() => setAddFormData({})}>Temizle</button>
        </div>
      </div>
    );
  }

  function renderSql() {
    return (
      <div>
        <h4 style={{color:'#1e293b', marginBottom:12}}>SQL Sorgusu Calistir</h4>
        <p style={{fontSize:12, color:'#64748b', marginBottom:12}}>SELECT, INSERT, UPDATE, DELETE sorgulari calistirabilirsiniz.</p>
        <textarea style={{...S.input, height:120, fontFamily:'monospace', resize:'vertical'}} value={sqlQuery} onChange={e => setSqlQuery(e.target.value)} placeholder="SELECT * FROM public.parks LIMIT 10;" />
        <div style={{marginTop:12}}>
          <button style={S.btn()} onClick={handleRunSql} disabled={sqlLoading}>{sqlLoading ? 'Calisiyor...' : 'Calistir'}</button>
        </div>
        {sqlResult && (
          <div style={{marginTop:20}}>
            {sqlResult.type==='write' ? (
              <div style={S.alert('ok')}>{sqlResult.message} ({sqlResult.rowCount} satir etkilendi)</div>
            ) : sqlResult.rows?.length > 0 ? (
              <div style={{overflowX:'auto', border:'1px solid #e2e8f0', borderRadius:8, maxHeight:400}}>
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:12}}>
                  <thead><tr style={{background:'#f8fafc'}}>{sqlResult.columns.map((c,i) => <th key={i} style={{padding:'8px 10px', textAlign:'left', fontWeight:600, color:'#475569', borderBottom:'2px solid #e2e8f0', whiteSpace:'nowrap'}}>{c}</th>)}</tr></thead>
                  <tbody>{sqlResult.rows.map((r,ri) => <tr key={ri} style={{borderBottom:'1px solid #f1f5f9'}}>{sqlResult.columns.map((c,ci) => <td key={ci} style={{padding:'6px 10px', color:'#334155', maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{r[c]!==null&&r[c]!==undefined?String(r[c]).substring(0,60):<span style={{color:'#cbd5e1'}}>null</span>}</td>)}</tr>)}</tbody>
                </table>
              </div>
            ) : <p style={{color:'#94a3b8', fontSize:13}}>Sonuc yok.</p>}
          </div>
        )}
      </div>
    );
  }

  function renderCompare() {
    return (
      <div>
        <h4 style={{color:'#1e293b', marginBottom:12}}>Tablo Kiyaslama</h4>
        <p style={{fontSize:12, color:'#64748b', marginBottom:16}}>Secili tablo ({selSchema}.{selTable}) ile baska bir tabloyu kiyaslayin.</p>
        <div style={{display:'flex', gap:12, alignItems:'center', marginBottom:20, flexWrap:'wrap'}}>
          <select style={{...S.input, maxWidth:180}} value={cmpSchema2} onChange={e => setCmpSchema2(e.target.value)}>
            {schemas.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select style={{...S.input, maxWidth:220}} value={cmpTable2||''} onChange={e => setCmpTable2(e.target.value)}>
            <option value="">Tablo secin...</option>
            {tables2.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button style={S.btn()} onClick={handleCompare} disabled={cmpLoading||!cmpTable2}>{cmpLoading?'Kiyaslaniyor...':'Kiyasla'}</button>
        </div>
        {cmpResult && (
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20}}>
            {[cmpResult.table1, cmpResult.table2].map((t,i) => (
              <div key={i} style={{background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:16}}>
                <h5 style={{margin:'0 0 8px', color:'#1e293b'}}>{t.schema}.{t.name}</h5>
                <p style={{fontSize:12, color:'#64748b', margin:'0 0 8px'}}>{t.rowCount} kayit | {t.columns.length} kolon</p>
                <div style={{display:'flex', flexWrap:'wrap', gap:4}}>
                  {t.columns.map((c,ci) => {
                    const isCommon = cmpResult.commonColumns.includes(c.column_name);
                    const isOnly = i===0 ? cmpResult.onlyInTable1.includes(c.column_name) : cmpResult.onlyInTable2.includes(c.column_name);
                    return <span key={ci} style={{fontSize:11, padding:'3px 8px', borderRadius:4, background: isOnly?'#fef2f2':isCommon?'#f0fdf4':'#f1f5f9', color: isOnly?'#dc2626':isCommon?'#16a34a':'#64748b', fontWeight:500}}>{c.column_name}</span>;
                  })}
                </div>
              </div>
            ))}
            <div style={{gridColumn:'1/-1', background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:16}}>
              <h5 style={{margin:'0 0 12px', color:'#1e293b'}}>Kiyaslama Sonucu</h5>
              <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12}}>
                <div style={{background:'#f0fdf4', padding:12, borderRadius:8, textAlign:'center'}}>
                  <div style={{fontSize:24, fontWeight:700, color:'#16a34a'}}>{cmpResult.commonColumns.length}</div>
                  <div style={{fontSize:12, color:'#166534'}}>Ortak Kolon</div>
                </div>
                <div style={{background:'#fef2f2', padding:12, borderRadius:8, textAlign:'center'}}>
                  <div style={{fontSize:24, fontWeight:700, color:'#dc2626'}}>{cmpResult.onlyInTable1.length}</div>
                  <div style={{fontSize:12, color:'#991b1b'}}>Sadece Tablo 1</div>
                </div>
                <div style={{background:'#eff6ff', padding:12, borderRadius:8, textAlign:'center'}}>
                  <div style={{fontSize:24, fontWeight:700, color:'#2563eb'}}>{cmpResult.onlyInTable2.length}</div>
                  <div style={{fontSize:12, color:'#1e40af'}}>Sadece Tablo 2</div>
                </div>
              </div>
              {cmpResult.typeMismatches.length > 0 && (
                <div style={{marginTop:12}}>
                  <p style={{fontSize:12, fontWeight:600, color:'#f59e0b', marginBottom:6}}>Tip Uyumsuzluklari:</p>
                  {cmpResult.typeMismatches.map((m,i) => <p key={i} style={{fontSize:12, color:'#64748b', margin:2}}>{m.column}: {m.type1} vs {m.type2}</p>)}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderCreate() {
    const handleCreateSchema = async () => {
      if (!newSchemaName.trim()) return;
      try {
        setCreateStatus(null); setError(null);
        await databaseService.executeWriteQuery(`CREATE SCHEMA IF NOT EXISTS "${newSchemaName.trim()}"`);
        setSuccess(`"${newSchemaName}" semasi olusturuldu!`);
        setNewSchemaName('');
        loadSchemas();
        setTimeout(() => setSuccess(null), 3000);
      } catch (e) { setError(e.message); }
    };

    const handleCreateTable = async () => {
      if (!newTableName.trim() || newTableCols.filter(c => c.name.trim()).length === 0) return;
      try {
        setCreateStatus(null); setError(null);
        const cols = newTableCols.filter(c => c.name.trim()).map(c => `"${c.name.trim()}" ${c.type || 'TEXT'}`).join(', ');
        const q = `CREATE TABLE IF NOT EXISTS "${newTableSchema}"."${newTableName.trim()}" (${cols})`;
        await databaseService.executeWriteQuery(q);
        setSuccess(`"${newTableSchema}.${newTableName}" tablosu olusturuldu!`);
        setNewTableName('');
        setNewTableCols([{name:'id', type:'SERIAL PRIMARY KEY'}, {name:'', type:'TEXT'}]);
        if (newTableSchema === selSchema) loadTables(selSchema);
        setTimeout(() => setSuccess(null), 3000);
      } catch (e) { setError(e.message); }
    };

    const CITIES_TR = [
      'Adana','Adiyaman','Afyonkarahisar','Agri','Amasya','Ankara','Antalya','Artvin','Aydin','Balikesir',
      'Bilecik','Bingol','Bitlis','Bolu','Burdur','Bursa','Canakkale','Cankiri','Corum','Denizli',
      'Diyarbakir','Edirne','Elazig','Erzincan','Erzurum','Eskisehir','Gaziantep','Giresun','Gumushane','Hakkari',
      'Hatay','Isparta','Mersin','Istanbul','Izmir','Kars','Kastamonu','Kayseri','Kirklareli','Kirsehir',
      'Kocaeli','Konya','Kutahya','Malatya','Manisa','Kahramanmaras','Mardin','Mugla','Mus','Nevsehir',
      'Nigde','Ordu','Rize','Sakarya','Samsun','Siirt','Sinop','Sivas','Tekirdag','Tokat',
      'Trabzon','Tunceli','Sanliurfa','Usak','Van','Yozgat','Zonguldak','Aksaray','Bayburt','Karaman',
      'Kirikkale','Batman','Sirnak','Bartin','Ardahan','Igdir','Yalova','Karabuk','Kilis','Osmaniye','Duzce'
    ];

    const handleAddAllCities = async () => {
      try {
        setCreateStatus({ type: 'loading', message: 'Sehirler ekleniyor...' });
        setError(null);
        let added = 0;
        for (let i = 0; i < CITIES_TR.length; i++) {
          try {
            await databaseService.executeWriteQuery(
              `INSERT INTO water_billing.city (id, city_name) VALUES (${i + 1}, '${CITIES_TR[i]}') ON CONFLICT (id) DO UPDATE SET city_name = '${CITIES_TR[i]}'`
            );
            added++;
          } catch { /* skip duplicates */ }
        }
        setCreateStatus({ type: 'ok', message: `${added} sehir eklendi / guncellendi!` });
        setTimeout(() => setCreateStatus(null), 5000);
      } catch (e) { setError(e.message); setCreateStatus(null); }
    };

    const handleAddCity = async (cityName) => {
      if (!cityName.trim()) return;
      try {
        setError(null);
        const maxRes = await databaseService.executeQuery('SELECT COALESCE(MAX(id), 0) + 1 as next_id FROM water_billing.city');
        const nextId = maxRes.rows?.[0]?.next_id || 1;
        await databaseService.executeWriteQuery(`INSERT INTO water_billing.city (id, city_name) VALUES (${nextId}, '${cityName.replace(/'/g, "''").trim()}')`);
        setSuccess(`"${cityName}" eklendi! (ID: ${nextId})`);
        setTimeout(() => setSuccess(null), 3000);
      } catch (e) { setError(e.message); }
    };

    const colTypes = ['TEXT','INTEGER','NUMERIC','BOOLEAN','DATE','TIMESTAMP','DOUBLE PRECISION','SERIAL PRIMARY KEY','BIGINT','VARCHAR(255)','JSONB'];

    return (
      <div style={{maxWidth:800}}>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24}}>
          <div style={{background:'#fff', borderRadius:10, padding:20, border:'1px solid #e2e8f0'}}>
            <h4 style={{color:'#1e293b', margin:'0 0 12px'}}>Yeni Sema Olustur</h4>
            <p style={{fontSize:12, color:'#64748b', margin:'0 0 12px'}}>Veritabanina yeni bir bolum (sema) ekleyin.</p>
            <div style={{display:'flex', gap:8}}>
              <input style={{...S.input, flex:1}} placeholder="Sema adi (orn: istanbul)" value={newSchemaName} onChange={e => setNewSchemaName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleCreateSchema(); }} />
              <button style={S.btn('#7c3aed')} onClick={handleCreateSchema}>Olustur</button>
            </div>
          </div>

          <div style={{background:'#fff', borderRadius:10, padding:20, border:'1px solid #e2e8f0'}}>
            <h4 style={{color:'#1e293b', margin:'0 0 12px'}}>Hizli Sehir Ekle</h4>
            <p style={{fontSize:12, color:'#64748b', margin:'0 0 12px'}}>water_billing.city tablosuna sehir ekleyin.</p>
            <div style={{display:'flex', gap:8, marginBottom:8}}>
              <input style={{...S.input, flex:1}} placeholder="Sehir adi (orn: Istanbul)" id="cityInput" onKeyDown={e => { if (e.key === 'Enter') { handleAddCity(e.target.value); e.target.value = ''; } }} />
              <button style={S.btn('#10b981')} onClick={() => { const inp = document.getElementById('cityInput'); handleAddCity(inp.value); inp.value = ''; }}>Ekle</button>
            </div>
            <button style={{...S.btn(createStatus?.type === 'loading' ? '#94a3b8' : '#3b82f6'), width:'100%', fontSize:12}} onClick={handleAddAllCities} disabled={createStatus?.type === 'loading'}>
              {createStatus?.type === 'loading' ? createStatus.message : 'Tum 81 Ili Ekle'}
            </button>
            {createStatus?.type === 'ok' && <div style={{marginTop:8, fontSize:12, color:'#16a34a', fontWeight:600}}>{createStatus.message}</div>}
          </div>
        </div>

        <div style={{background:'#fff', borderRadius:10, padding:20, border:'1px solid #e2e8f0', marginBottom:24}}>
          <h4 style={{color:'#1e293b', margin:'0 0 12px'}}>Yeni Tablo Olustur</h4>
          <p style={{fontSize:12, color:'#64748b', margin:'0 0 12px'}}>Secilen semada yeni bir tablo olusturun ve kolonlari tanimlayin.</p>

          <div style={{display:'grid', gridTemplateColumns:'1fr 2fr', gap:'12px 16px', alignItems:'center', marginBottom:16}}>
            <label style={{fontSize:13, fontWeight:600, color:'#475569'}}>Sema</label>
            <select style={S.input} value={newTableSchema} onChange={e => setNewTableSchema(e.target.value)}>
              {schemas.map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            <label style={{fontSize:13, fontWeight:600, color:'#475569'}}>Tablo Adi</label>
            <input style={S.input} placeholder="orn: parks, buildings, sensors" value={newTableName} onChange={e => setNewTableName(e.target.value)} />
          </div>

          <div style={{marginBottom:16}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
              <label style={{fontSize:13, fontWeight:600, color:'#475569'}}>Kolonlar</label>
              <button style={S.btnSm('#3b82f6')} onClick={() => setNewTableCols(p => [...p, {name:'', type:'TEXT'}])}>+ Kolon Ekle</button>
            </div>
            <div style={{display:'grid', gap:8}}>
              {newTableCols.map((col, i) => (
                <div key={i} style={{display:'flex', gap:8, alignItems:'center'}}>
                  <span style={{fontSize:12, color:'#94a3b8', width:24, textAlign:'center'}}>{i+1}</span>
                  <input style={{...S.input, flex:1}} placeholder="Kolon adi" value={col.name} onChange={e => { const c = [...newTableCols]; c[i].name = e.target.value; setNewTableCols(c); }} />
                  <select style={{...S.input, flex:1}} value={col.type} onChange={e => { const c = [...newTableCols]; c[i].type = e.target.value; setNewTableCols(c); }}>
                    {colTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {i > 0 && <button style={S.btnSm('#ef4444')} onClick={() => setNewTableCols(p => p.filter((_,j) => j !== i))}>Sil</button>}
                </div>
              ))}
            </div>
          </div>

          <div style={{background:'#f8fafc', borderRadius:8, padding:12, marginBottom:16, fontFamily:'monospace', fontSize:12, color:'#475569'}}>
            <span style={{color:'#7c3aed', fontWeight:600}}>CREATE TABLE</span> {newTableSchema}.{newTableName || '...'} (
            {newTableCols.filter(c=>c.name.trim()).map(c => `"${c.name}" ${c.type}`).join(', ')}
            )
          </div>

          <button style={S.btn('#7c3aed')} onClick={handleCreateTable}>Tabloyu Olustur</button>
        </div>

        <div style={{background:'#fff', borderRadius:10, padding:20, border:'1px solid #e2e8f0'}}>
          <h4 style={{color:'#1e293b', margin:'0 0 12px'}}>Sema Yonetimi — Ornek Yapilar</h4>
          <p style={{fontSize:12, color:'#64748b', margin:'0 0 16px'}}>Yeni bir sehir icin hazir tablo yapisi olusturun.</p>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12}}>
            {[
              {name: 'Park Yapisi', desc: 'park_id, name, latitude, longitude, total_area', schema: 'park', cols: 'park_id SERIAL PRIMARY KEY, name TEXT, latitude DOUBLE PRECISION, longitude DOUBLE PRECISION, total_area_m2 NUMERIC, grass_area_m2 NUMERIC, hard_surface_area_m2 NUMERIC'},
              {name: 'Abone Yapisi', desc: 'subscriber_id, name, park_id, meter_no', schema: 'subscriber', cols: 'subscriber_id SERIAL PRIMARY KEY, name TEXT, park_id INTEGER, meter_no TEXT, address TEXT'},
              {name: 'Fatura Yapisi', desc: 'bill_id, subscriber_id, amount, period', schema: 'bill', cols: 'bill_id SERIAL PRIMARY KEY, subscriber_id INTEGER, amount NUMERIC, period TEXT, consumption_m3 NUMERIC, billing_date DATE'},
            ].map((tmpl, i) => (
              <div key={i} style={{background:'#f8fafc', borderRadius:8, padding:16, border:'1px solid #e2e8f0'}}>
                <h5 style={{margin:'0 0 6px', color:'#1e293b', fontSize:13}}>{tmpl.name}</h5>
                <p style={{fontSize:11, color:'#64748b', margin:'0 0 10px'}}>{tmpl.desc}</p>
                <button style={{...S.btnSm('#6366f1'), width:'100%'}} onClick={async () => {
                  const schemaName = prompt('Hangi semaya olusturulsun? (orn: istanbul)');
                  if (!schemaName) return;
                  try {
                    setError(null);
                    await databaseService.executeWriteQuery(`CREATE SCHEMA IF NOT EXISTS "${schemaName}"`);
                    await databaseService.executeWriteQuery(`CREATE TABLE IF NOT EXISTS "${schemaName}"."${tmpl.schema}" (${tmpl.cols})`);
                    setSuccess(`${schemaName}.${tmpl.schema} tablosu olusturuldu!`);
                    loadSchemas();
                    setTimeout(() => setSuccess(null), 3000);
                  } catch (e) { setError(e.message); }
                }}>
                  Sema + Tablo Olustur
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
};

export default DatabaseExplorer;
